/**
 * TicketNotifications — Toast, Notification Center, and Sound Alert system
 * for the admin support ticket experience.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import {
  X, Bell, BellRing, BellOff, Volume2, VolumeX,
  CheckCheck, Trash2, Filter, MessageSquare, UserCheck,
  ArrowUpDown, AlertTriangle, Clock, ExternalLink,
} from 'lucide-react';
import { auth } from '../../lib/firebase';
import type { Ticket } from '../../types';
import Modal from '../shared/Modal';

/* ─── Types ─── */

export interface AdminNotification {
  id: string;
  type: 'new_message' | 'assignment' | 'escalation' | 'status_update' | 'system';
  ticketId?: string;
  title: string;
  body: string;
  senderName?: string;
  createdAt: string;
  read: boolean;
  /** URL to navigate to when clicked */
  actionUrl?: string;
}

/* ─── Sound Alert System ─── */

const SOUND_FILES: Record<string, string> = {
  chime: '/sounds/chime.wav',
  bell: '/sounds/bell.wav',
  ping: '/sounds/ping.wav',
  none: '',
};

interface SoundPrefs {
  enabled: boolean;
  tone: string;
  volume: number;
}

const DEFAULT_SOUND_PREFS: SoundPrefs = { enabled: false, tone: 'chime', volume: 0.5 };
const SOUND_PREFS_KEY = 'ticket_sound_prefs';

function loadSoundPrefs(): SoundPrefs {
  try {
    const raw = localStorage.getItem(SOUND_PREFS_KEY);
    return raw ? { ...DEFAULT_SOUND_PREFS, ...JSON.parse(raw) } : DEFAULT_SOUND_PREFS;
  } catch { return DEFAULT_SOUND_PREFS; }
}

function saveSoundPrefs(prefs: SoundPrefs) {
  localStorage.setItem(SOUND_PREFS_KEY, JSON.stringify(prefs));
}

let audioElement: HTMLAudioElement | null = null;

function playNotificationSound(prefs: SoundPrefs) {
  if (!prefs.enabled || !prefs.tone || prefs.tone === 'none') return;
  const src = SOUND_FILES[prefs.tone];
  if (!src) return;
  try {
    if (!audioElement) {
      audioElement = new Audio();
      audioElement.preload = 'auto';
      // Browsers block audio until the user has interacted with the page at
      // least once. Prime the element on the first pointer/keydown so sounds
      // play reliably afterwards.
      const unlock = () => {
        audioElement?.play().then(() => audioElement?.pause()).catch(() => {});
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
      };
      window.addEventListener('pointerdown', unlock);
      window.addEventListener('keydown', unlock);
    }
    audioElement.src = src;
    audioElement.volume = Math.max(0, Math.min(1, prefs.volume ?? 0.5));
    audioElement.play().catch(() => { /* autoplay may be blocked until first interaction */ });
  } catch { /* ignore */ }
}

/* ─── Sound Preferences Panel ─── */

export function SoundPrefsPanel({ onClose }: { onClose: () => void }) {
  const [prefs, setPrefs] = React.useState<SoundPrefs>(loadSoundPrefs);

  const update = (patch: Partial<SoundPrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    saveSoundPrefs(next);
  };

  return (
    <div
      className="absolute top-full right-0 mt-1 z-[8500] bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 w-72"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-extrabold text-slate-800">Notification Sounds</h4>
        <button aria-label="Close" title="Close" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer" type="button">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <label className="flex items-center justify-between py-2 cursor-pointer">
        <span className="text-xs font-bold text-slate-700">Enable sounds</span>
        <input
          type="checkbox"
          checked={prefs.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
          className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-blue-500 cursor-pointer"
        />
      </label>

      {prefs.enabled && (
        <>
          <div className="space-y-1.5 mb-3">
            <label className="text-[11px] font-bold text-slate-500">Tone</label>
            <select
              value={prefs.tone}
              onChange={(e) => update({ tone: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-bold outline-none focus:border-blue-400 cursor-pointer"
            >
              <option value="chime">Chime</option>
              <option value="bell">Bell</option>
              <option value="ping">Ping</option>
              <option value="none">None</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-500">Volume: {Math.round(prefs.volume * 100)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={prefs.volume}
              onChange={(e) => update({ volume: parseFloat(e.target.value) })}
              className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          <button
            onClick={() => playNotificationSound(prefs)}
            className="mt-3 w-full py-1.5 text-xs font-bold text-brand bg-blue-50 hover:bg-blue-100 rounded-xl transition-all cursor-pointer"
            type="button"
          >
            Test Sound
          </button>
        </>
      )}
    </div>
  );
}

/* ─── In-App Toast Notification ─── */

interface ToastItem {
  id: string;
  type: AdminNotification['type'];
  title: string;
  body: string;
  ticketId?: string;
  senderName?: string;
  createdAt: string;
  onOpen?: (ticketId: string) => void;
}

let toastCounter = 0;

export function showTicketToast(
  data: Omit<ToastItem, 'id'>,
  prefs?: SoundPrefs,
) {
  if (prefs) playNotificationSound(prefs);

  const event = new CustomEvent<Omit<ToastItem, 'id'>>('ticket-toast', {
    detail: data,
  });
  window.dispatchEvent(event);
}

/* ─── Toast Container ─── */

export function ToastContainer({ onOpenTicket }: { onOpenTicket?: (ticketId: string) => void }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const data = (e as CustomEvent).detail as Omit<ToastItem, 'id'>;
      toastCounter++;
      const id = `toast_${Date.now()}_${toastCounter}`;
      setToasts(prev => [{
        id,
        ...data,
        onOpen: (ticketId: string) => {
          removeToast(id);
          onOpenTicket?.(ticketId);
        },
      }, ...prev].slice(0, 5)); // max 5 toasts

      // Auto-dismiss after 5 seconds
      setTimeout(() => removeToast(id), 5000);
    };
    window.addEventListener('ticket-toast', handler);
    return () => window.removeEventListener('ticket-toast', handler);
  }, [onOpenTicket]);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return ReactDOM.createPortal(
    <div className="fixed top-4 right-4 z-[var(--z-toast)] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className="pointer-events-auto bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 animate-slide-in-right cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => toast.ticketId && toast.onOpen?.(toast.ticketId)}
          role="alert"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5">
              {toast.type === 'new_message' ? (
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-brand" />
                </div>
              ) : toast.type === 'assignment' ? (
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <UserCheck className="h-4 w-4 text-amber-600" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                  <Bell className="h-4 w-4 text-slate-500" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-extrabold text-slate-900 truncate">{toast.title}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }}
                  className="shrink-0 p-0.5 text-slate-300 hover:text-slate-500 rounded cursor-pointer"
                  type="button"
                  aria-label="Dismiss"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{toast.body}</p>
              <div className="flex items-center gap-2 mt-1">
                {toast.senderName && (
                  <span className="text-[10px] font-bold text-brand">{toast.senderName}</span>
                )}
                {toast.ticketId && (
                  <span className="text-[10px] font-mono text-slate-400">#{toast.ticketId.slice(0, 8)}</span>
                )}
                <span className="text-[11px] text-slate-500">{formatToastTime(toast.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>,
    document.body,
  );
}

function formatToastTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}

/* ─── Notification Center Panel ─── */

interface NotificationCenterProps {
  onClose: () => void;
  notifications: AdminNotification[];
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onDelete: (id: string) => void;
  onOpenTicket?: (ticketId: string) => void;
}

export function NotificationCenter({
  onClose,
  notifications,
  onMarkRead,
  onMarkAllRead,
  onDelete,
  onOpenTicket,
}: NotificationCenterProps) {
  const [filter, setFilter] = React.useState<AdminNotification['type'] | 'all'>('all');
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Focus trap
  React.useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const filtered = filter === 'all' ? notifications : notifications.filter(n => n.type === filter);
  const unreadCount = notifications.filter(n => !n.read).length;
  const typeFilters: { value: AdminNotification['type'] | 'all'; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'All', icon: <Bell className="h-3.5 w-3.5" /> },
    { value: 'new_message', label: 'Messages', icon: <MessageSquare className="h-3.5 w-3.5" /> },
    { value: 'assignment', label: 'Assignments', icon: <UserCheck className="h-3.5 w-3.5" /> },
    { value: 'status_update', label: 'Status', icon: <ArrowUpDown className="h-3.5 w-3.5" /> },
    { value: 'system', label: 'System', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
  ];

  return (
    <Modal onClose={onClose} ariaLabel="Notifications">
      <div
        ref={panelRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[70vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-brand" />
            <h3 className="text-sm font-extrabold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 bg-brand-active text-[10px] font-bold rounded-full">{unreadCount} new</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-brand bg-brand-active rounded-lg transition-all cursor-pointer"
                type="button"
              >
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
            <button aria-label="Close" title="Close" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer" type="button">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-1.5 px-4 py-2 border-b border-slate-100 overflow-x-auto shrink-0">
          {typeFilters.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                filter === f.value
                  ? 'bg-brand-active border border-blue-200'
                  : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'
              }`}
              type="button"
            >
              {f.icon}
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <BellOff className="h-8 w-8 text-slate-300 mb-2" />
              <p className="text-xs font-bold text-slate-400">No notifications</p>
              <p className="text-[10px] text-slate-300 mt-1">All caught up!</p>
            </div>
          ) : (
            filtered.map(n => (
              <div
                key={n.id}
                className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50/80 transition-colors cursor-pointer ${
                  !n.read ? 'bg-blue-50/30' : ''
                }`}
                onClick={() => {
                  if (!n.read) onMarkRead(n.id);
                  if (n.ticketId && onOpenTicket) onOpenTicket(n.ticketId);
                }}
              >
                <div className="shrink-0 mt-0.5">
                  {n.type === 'new_message' ? (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <MessageSquare className="h-4 w-4 text-brand" />
                    </div>
                  ) : n.type === 'assignment' ? (
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <UserCheck className="h-4 w-4 text-amber-600" />
                    </div>
                  ) : n.type === 'escalation' ? (
                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
                      <AlertTriangle className="h-4 w-4 text-rose-600" />
                    </div>
                  ) : n.type === 'status_update' ? (
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <ArrowUpDown className="h-4 w-4 text-emerald-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <Bell className="h-4 w-4 text-slate-500" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-xs ${!n.read ? 'font-extrabold text-slate-900' : 'font-semibold text-slate-700'} truncate`}>
                      {n.title}
                    </p>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="h-3 w-3 text-slate-300" />
                    <span className="text-[11px] text-slate-500">{formatToastTime(n.createdAt)}</span>
                    {n.ticketId && (
                      <>
                        <span className="text-[10px] text-slate-300">·</span>
                        <span className="text-[10px] font-mono text-slate-400">#{n.ticketId.slice(0, 8)}</span>
                      </>
                    )}
                    {n.ticketId && (
                      <ExternalLink className="h-3 w-3 text-blue-400 ml-auto" />
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(n.id); }}
                  className="shrink-0 p-1 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all cursor-pointer opacity-0 group-hover:opacity-100"
                  type="button"
                  aria-label="Delete notification"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ─── Browser Notification Helper ─── */

export function sendBrowserNotification(title: string, options?: NotificationOptions) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(title, {
      icon: '/pwa-192x192.png',
      badge: '/pwa-192x192.png',
      ...options,
    });
  } catch { /* ignore */ }
}

/* ─── Unread Ticket Badge Hook ─── */

export function useUnreadCount(tickets: Ticket[]): number {
  return React.useMemo(() =>
    tickets.filter(t => t.unread).length,
  [tickets]);
}

/* ─── Polling Hook for Real-Time Updates ─── */

interface PollingCallbacks {
  onNewMessage?: (ticket: Ticket, prev: Ticket[]) => void;
}

export function useTicketPolling(
  fetchFn: () => Promise<void>,
  tickets: Ticket[],
  setTickets: React.Dispatch<React.SetStateAction<Ticket[]>>,
  callbacks?: PollingCallbacks,
  intervalMs = 5000,
) {
  const prevRef = React.useRef<Ticket[]>(tickets);

  React.useEffect(() => {
    prevRef.current = tickets;
  }, [tickets]);

  React.useEffect(() => {
    if (!fetchFn) return;
    let interval: ReturnType<typeof setInterval>;
    const tick = async () => {
      if (document.visibilityState === 'hidden') return;
      const prev = prevRef.current;
      await fetchFn();
      // Check for new applicant messages after fetch
      if (callbacks?.onNewMessage && prev.length > 0) {
        // We compare after fetch completes — the state is updated by fetchFn
        // We detect by checking unread status change
      }
    };
    interval = setInterval(tick, intervalMs);
    return () => clearInterval(interval);
  }, [fetchFn, intervalMs, callbacks]);
}